/**
 * Disciplines et pouvoirs (livre de base V5 + amalgames courants).
 * Le Néant (Oblivion, Hecata et Lasombra) vient d'un supplément : pouvoirs saisis de mémoire, coûts à vérifier, rituels absents.
 * Données mécaniques uniquement (niveau, coût, réserve) : pas de texte de règles, pour respecter le Dark Pack.
 * Les noms français des pouvoirs sont à confronter à la VF officielle.
 *
 * Format d'un pouvoir :
 * - rouse : nombre de tests d'exaltation (0 = gratuit)
 * - pool : réserve, liste de traits ; un tableau imbriqué = au choix (ex : [['charisma', 'manipulation'], 'animalism'])
 * - resist : réserve d'opposition éventuelle, même format
 * - amalgam : discipline secondaire requise ({discipline, level})
 */
import {localized} from './traits';

export const DISCIPLINES = [
  {id: 'animalism', name: {fr: 'Animalisme', en: 'Animalism'}},
  {id: 'auspex', name: {fr: 'Auspex', en: 'Auspex'}},
  {id: 'bloodSorcery', name: {fr: 'Magie du sang', en: 'Blood Sorcery'}},
  {id: 'celerity', name: {fr: 'Célérité', en: 'Celerity'}},
  {id: 'dominate', name: {fr: 'Domination', en: 'Dominate'}},
  {id: 'fortitude', name: {fr: "Force d'âme", en: 'Fortitude'}},
  {id: 'obfuscate', name: {fr: 'Occultation', en: 'Obfuscate'}},
  {id: 'oblivion', name: {fr: 'Néant', en: 'Oblivion'}},
  {id: 'potence', name: {fr: 'Puissance', en: 'Potence'}},
  {id: 'presence', name: {fr: 'Présence', en: 'Presence'}},
  {id: 'protean', name: {fr: 'Protéisme', en: 'Protean'}},
  {id: 'thinBloodAlchemy', name: {fr: 'Alchimie du Sang-Clair', en: 'Thin-Blood Alchemy'}},
];

const power = (id, discipline, level, fr, en, details = {}) => ({
  id,
  discipline,
  level,
  name: {fr, en},
  rouse: 0,
  pool: null,
  resist: null,
  amalgam: null,
  ...details,
});

export const POWERS = [
  // Animalisme
  power('bondFamulus', 'animalism', 1, 'Lien du familier', 'Bond Famulus', {pool: ['charisma', 'animalKen']}),
  power('senseTheBeast', 'animalism', 1, 'Sentir la Bête', 'Sense the Beast', {pool: ['resolve', 'animalism'], resist: ['composure', 'subterfuge']}),
  power('feralWhispers', 'animalism', 2, 'Murmures sauvages', 'Feral Whispers', {rouse: 1, pool: [['manipulation', 'charisma'], 'animalism']}),
  power('animalSucculence', 'animalism', 3, 'Succulence animale', 'Animal Succulence'),
  power('quellTheBeast', 'animalism', 3, 'Apaiser la Bête', 'Quell the Beast', {rouse: 1, pool: ['charisma', 'animalism'], resist: ['stamina', 'resolve']}),
  power('unlivingHive', 'animalism', 3, 'Ruche non-morte', 'Unliving Hive', {amalgam: {discipline: 'obfuscate', level: 2}}),
  power('subsumeTheSpirit', 'animalism', 4, "Posséder l'esprit", 'Subsume the Spirit', {rouse: 1, pool: ['manipulation', 'animalism']}),
  power('animalDominion', 'animalism', 5, 'Domination animale', 'Animal Dominion', {rouse: 2, pool: ['charisma', 'animalism']}),
  power('drawingOutTheBeast', 'animalism', 5, 'Extraire la Bête', 'Drawing Out the Beast', {rouse: 1, pool: ['wits', 'animalism'], resist: ['composure', 'resolve']}),

  // Auspex
  power('heightenedSenses', 'auspex', 1, 'Sens accrus', 'Heightened Senses', {pool: [['wits', 'resolve'], 'auspex']}),
  power('senseTheUnseen', 'auspex', 1, "Percevoir l'invisible", 'Sense the Unseen', {pool: [['wits', 'resolve'], 'auspex']}),
  power('premonition', 'auspex', 2, 'Prémonition', 'Premonition', {pool: ['resolve', 'auspex']}),
  power('scryTheSoul', 'auspex', 3, "Sonder l'âme", 'Scry the Soul', {rouse: 1, pool: ['intelligence', 'auspex'], resist: ['composure', 'subterfuge']}),
  power('shareTheSenses', 'auspex', 3, 'Partager les sens', 'Share the Senses', {rouse: 1, pool: ['resolve', 'auspex']}),
  power('spiritsTouch', 'auspex', 4, "Toucher de l'esprit", "Spirit's Touch", {rouse: 1, pool: ['intelligence', 'auspex']}),
  power('clairvoyance', 'auspex', 5, 'Clairvoyance', 'Clairvoyance', {rouse: 1, pool: ['intelligence', 'auspex']}),
  power('possession', 'auspex', 5, 'Possession', 'Possession', {rouse: 2, pool: ['resolve', 'auspex'], resist: ['resolve', 'intelligence'], amalgam: {discipline: 'dominate', level: 3}}),
  power('telepathy', 'auspex', 5, 'Télépathie', 'Telepathy', {rouse: 1, pool: ['resolve', 'auspex'], resist: ['wits', 'subterfuge']}),

  // Magie du sang
  power('corrosiveVitae', 'bloodSorcery', 1, 'Vitae corrosive', 'Corrosive Vitae', {rouse: 1}),
  power('aTasteForBlood', 'bloodSorcery', 1, 'Goût du sang', 'A Taste for Blood', {rouse: 1, pool: ['resolve', 'bloodSorcery']}),
  power('extinguishVitae', 'bloodSorcery', 2, 'Éteindre la vitae', 'Extinguish Vitae', {rouse: 1, pool: ['intelligence', 'bloodSorcery'], resist: ['stamina', 'composure']}),
  power('bloodOfPotency', 'bloodSorcery', 3, 'Sang de puissance', 'Blood of Potency', {rouse: 1, pool: ['resolve', 'bloodSorcery']}),
  power('scorpionsTouch', 'bloodSorcery', 3, 'Toucher du scorpion', "Scorpion's Touch", {rouse: 1, pool: ['strength', 'bloodSorcery'], resist: ['stamina', ['occult', 'fortitude']]}),
  power('theftOfVitae', 'bloodSorcery', 4, 'Vol de vitae', 'Theft of Vitae', {rouse: 1, pool: ['wits', 'bloodSorcery'], resist: ['wits', 'occult']}),
  power('baalsCaress', 'bloodSorcery', 5, 'Caresse de Baal', "Baal's Caress", {rouse: 1, pool: ['strength', 'bloodSorcery'], resist: ['stamina', ['occult', 'fortitude']]}),
  power('cauldronOfBlood', 'bloodSorcery', 5, 'Chaudron de sang', 'Cauldron of Blood', {rouse: 1, pool: ['resolve', 'bloodSorcery'], resist: ['composure', 'occult']}),

  // Célérité
  power('catsGrace', 'celerity', 1, 'Grâce féline', "Cat's Grace"),
  power('rapidReflexes', 'celerity', 1, 'Réflexes éclair', 'Rapid Reflexes'),
  power('fleetness', 'celerity', 2, 'Vélocité', 'Fleetness', {rouse: 1}),
  power('blink', 'celerity', 3, 'Clignement', 'Blink', {rouse: 1, pool: ['dexterity', 'athletics']}),
  power('traversal', 'celerity', 3, 'Traversée', 'Traversal', {rouse: 1, pool: ['dexterity', 'athletics']}),
  power('draughtOfElegance', 'celerity', 4, "Breuvage d'élégance", 'Draught of Elegance', {rouse: 1}),
  power('unerringAim', 'celerity', 4, 'Visée infaillible', 'Unerring Aim', {rouse: 1, amalgam: {discipline: 'auspex', level: 2}}),
  power('lightningStrike', 'celerity', 5, 'Frappe éclair', 'Lightning Strike', {rouse: 1}),
  power('splitSecond', 'celerity', 5, 'Fraction de seconde', 'Split Second', {rouse: 1}),

  // Domination
  power('cloudMemory', 'dominate', 1, 'Brouiller la mémoire', 'Cloud Memory', {rouse: 1, pool: ['charisma', 'dominate'], resist: ['wits', 'resolve']}),
  power('compel', 'dominate', 1, 'Contraindre', 'Compel', {pool: ['charisma', 'dominate'], resist: ['intelligence', 'resolve']}),
  power('mesmerize', 'dominate', 2, 'Hypnotiser', 'Mesmerize', {rouse: 1, pool: ['manipulation', 'dominate'], resist: ['intelligence', 'resolve']}),
  power('dementation', 'dominate', 2, 'Démence', 'Dementation', {rouse: 1, pool: ['manipulation', 'dominate'], resist: ['composure', 'intelligence'], amalgam: {discipline: 'obfuscate', level: 2}}),
  power('forgetfulMind', 'dominate', 3, 'Esprit oublieux', 'Forgetful Mind', {rouse: 1, pool: ['manipulation', 'dominate'], resist: ['intelligence', 'resolve']}),
  power('submergedDirective', 'dominate', 3, 'Directive enfouie', 'Submerged Directive'),
  power('rationalize', 'dominate', 4, 'Rationaliser', 'Rationalize'),
  power('massManipulation', 'dominate', 5, 'Manipulation de masse', 'Mass Manipulation', {rouse: 1}),
  power('terminalDecree', 'dominate', 5, 'Décret ultime', 'Terminal Decree'),

  // Force d'âme
  power('resilience', 'fortitude', 1, 'Résilience', 'Resilience'),
  power('unswayableMind', 'fortitude', 1, 'Esprit inébranlable', 'Unswayable Mind'),
  power('toughness', 'fortitude', 2, 'Robustesse', 'Toughness', {rouse: 1}),
  power('enduringBeasts', 'fortitude', 2, 'Bêtes endurantes', 'Enduring Beasts', {rouse: 1, amalgam: {discipline: 'animalism', level: 1}}),
  power('defyBane', 'fortitude', 3, 'Défier le fléau', 'Defy Bane', {rouse: 1}),
  power('fortifyTheInnerFacade', 'fortitude', 3, 'Façade intérieure', 'Fortify the Inner Façade'),
  power('draughtOfEndurance', 'fortitude', 4, "Breuvage d'endurance", 'Draught of Endurance', {rouse: 1}),
  power('fleshOfMarble', 'fortitude', 5, 'Chair de marbre', 'Flesh of Marble', {rouse: 2}),
  power('prowessFromPain', 'fortitude', 5, 'La douleur pour force', 'Prowess from Pain', {rouse: 1}),

  // Occultation
  power('cloakOfShadows', 'obfuscate', 1, "Manteau d'ombres", 'Cloak of Shadows'),
  power('silenceOfDeath', 'obfuscate', 1, 'Silence de mort', 'Silence of Death'),
  power('unseenPassage', 'obfuscate', 2, 'Passage invisible', 'Unseen Passage', {rouse: 1, pool: ['wits', 'obfuscate'], resist: ['wits', 'awareness']}),
  power('ghostInTheMachine', 'obfuscate', 3, 'Fantôme dans la machine', 'Ghost in the Machine'),
  power('maskOfAThousandFaces', 'obfuscate', 3, 'Masque aux mille visages', 'Mask of a Thousand Faces', {rouse: 1}),
  power('conceal', 'obfuscate', 4, 'Dissimuler', 'Conceal', {rouse: 1, pool: ['intelligence', 'obfuscate']}),
  power('vanish', 'obfuscate', 4, 'Disparaître', 'Vanish', {pool: ['wits', 'obfuscate'], resist: ['wits', 'awareness']}),
  power('cloakTheGathering', 'obfuscate', 5, "Voiler l'assemblée", 'Cloak the Gathering', {rouse: 1}),
  power('impostorsGuise', 'obfuscate', 5, "Visage d'imposteur", "Impostor's Guise", {rouse: 1, pool: ['wits', 'obfuscate']}),

  // Puissance
  power('lethalBody', 'potence', 1, 'Corps létal', 'Lethal Body'),
  power('soaringLeap', 'potence', 1, 'Bond prodigieux', 'Soaring Leap'),
  power('prowess', 'potence', 2, 'Prouesse', 'Prowess', {rouse: 1}),
  power('brutalFeed', 'potence', 3, 'Festin brutal', 'Brutal Feed'),
  power('sparkOfRage', 'potence', 3, 'Étincelle de rage', 'Spark of Rage', {rouse: 1, pool: ['manipulation', 'potence'], resist: ['composure', 'resolve'], amalgam: {discipline: 'presence', level: 3}}),
  power('uncannyGrip', 'potence', 3, 'Poigne surnaturelle', 'Uncanny Grip', {rouse: 1}),
  power('draughtOfMight', 'potence', 4, 'Breuvage de puissance', 'Draught of Might', {rouse: 1}),
  power('earthshock', 'potence', 5, 'Séisme', 'Earthshock', {rouse: 2}),
  power('fistOfCaine', 'potence', 5, 'Poing de Caïn', 'Fist of Caine', {rouse: 1}),

  // Néant
  power('ashesToAshes', 'oblivion', 1, 'Cendres aux cendres', 'Ashes to Ashes', {rouse: 1}),
  power('bindingFetter', 'oblivion', 1, 'Lien spectral', 'Binding Fetter'),
  power('oblivionsSight', 'oblivion', 1, 'Regard du Néant', "Oblivion's Sight"),
  power('shadowCloak', 'oblivion', 1, "Manteau d'ombre", 'Shadow Cloak'),
  power('armsOfAhriman', 'oblivion', 2, "Bras d'Ahriman", 'Arms of Ahriman', {rouse: 1, amalgam: {discipline: 'potence', level: 2}}),
  power('fatalPrecognition', 'oblivion', 2, 'Prémonition fatale', 'Fatal Precognition', {rouse: 1, amalgam: {discipline: 'auspex', level: 2}}),
  power('shadowCast', 'oblivion', 2, "Projection d'ombre", 'Shadow Cast', {rouse: 1}),
  power('whereTheVeilThins', 'oblivion', 2, 'Là où le voile est mince', 'Where the Veil Thins'),
  power('auraOfDecay', 'oblivion', 3, 'Aura de décrépitude', 'Aura of Decay', {rouse: 1}),
  power('passionFeast', 'oblivion', 3, 'Festin de passion', 'Passion Feast'),
  power('shadowPerspective', 'oblivion', 3, "Point de vue de l'ombre", 'Shadow Perspective', {rouse: 1}),
  power('touchOfOblivion', 'oblivion', 3, 'Toucher du Néant', 'Touch of Oblivion', {rouse: 1}),
  power('necroticPlague', 'oblivion', 4, 'Peste nécrotique', 'Necrotic Plague', {rouse: 1}),
  power('shadowStep', 'oblivion', 4, "Pas de l'ombre", 'Shadow Step', {rouse: 1}),
  power('stygianShroud', 'oblivion', 4, 'Linceul stygien', 'Stygian Shroud', {rouse: 1}),
  power('skuldFulfilled', 'oblivion', 5, 'Skuld accompli', 'Skuld Fulfilled', {rouse: 2}),
  power('tenebrousAvatar', 'oblivion', 5, 'Avatar ténébreux', 'Tenebrous Avatar', {rouse: 2}),
  power('witheringSpirit', 'oblivion', 5, 'Esprit flétrissant', 'Withering Spirit', {rouse: 1}),

  // Présence
  power('awe', 'presence', 1, 'Fascination', 'Awe', {rouse: 1}),
  power('daunt', 'presence', 1, 'Intimider', 'Daunt', {rouse: 1}),
  power('lingeringKiss', 'presence', 2, 'Baiser persistant', 'Lingering Kiss'),
  power('dreadGaze', 'presence', 3, 'Regard terrifiant', 'Dread Gaze', {rouse: 1, pool: ['charisma', 'presence'], resist: ['composure', 'resolve']}),
  power('entrancement', 'presence', 3, 'Envoûtement', 'Entrancement', {rouse: 1, pool: ['charisma', 'presence'], resist: ['composure', 'wits']}),
  power('irresistibleVoice', 'presence', 4, 'Voix irrésistible', 'Irresistible Voice', {amalgam: {discipline: 'dominate', level: 1}}),
  power('summon', 'presence', 4, 'Convocation', 'Summon', {rouse: 1, pool: ['manipulation', 'presence'], resist: ['composure', 'intelligence']}),
  power('majesty', 'presence', 5, 'Majesté', 'Majesty', {rouse: 2, pool: ['charisma', 'presence'], resist: ['composure', 'resolve']}),
  power('starMagnetism', 'presence', 5, 'Magnétisme de star', 'Star Magnetism', {rouse: 1}),

  // Protéisme
  power('eyesOfTheBeast', 'protean', 1, 'Yeux de la Bête', 'Eyes of the Beast'),
  power('weightOfTheFeather', 'protean', 1, 'Poids de la plume', 'Weight of the Feather', {pool: ['wits', 'survival']}),
  power('feralWeapons', 'protean', 2, 'Armes sauvages', 'Feral Weapons', {rouse: 1}),
  power('earthMeld', 'protean', 3, 'Fusion avec la terre', 'Earth Meld', {rouse: 1}),
  power('shapechange', 'protean', 3, 'Métamorphose animale', 'Shapechange', {rouse: 1}),
  power('metamorphosis', 'protean', 4, 'Métamorphose', 'Metamorphosis', {rouse: 1}),
  power('mistForm', 'protean', 5, 'Forme de brume', 'Mist Form', {rouse: 1}),
  power('theUnfetteredHeart', 'protean', 5, 'Cœur sans entraves', 'The Unfettered Heart'),
];

export const DISCIPLINE_IDS = DISCIPLINES.map(discipline => discipline.id);
export const DISCIPLINES_BY_ID = DISCIPLINES.reduce((acc, discipline) => ({...acc, [discipline.id]: discipline}), {});
export const POWERS_BY_ID = POWERS.reduce((acc, item) => ({...acc, [item.id]: item}), {});

/**
 * Disciplines d'une fiche dans un ordre stable : niveau décroissant, puis nom (dans la langue affichée).
 * L'ordre des clés d'un objet Firestore change d'une écriture à l'autre : sans tri, la fiche se réorganise.
 * @param {Object} disciplines {id: {level, powers}}
 * @returns {Array} paires [id, discipline]
 */
export const sortDisciplineEntries = (disciplines, lng = 'fr') => {
  const name = id => localized(DISCIPLINES_BY_ID[id]?.name, lng) || id;
  return Object.entries(disciplines)
    .sort(([idA, a], [idB, b]) => ((b.level || 0) - (a.level || 0)) || name(idA).localeCompare(name(idB), lng));
};

/** Pouvoirs d'une discipline dans un ordre stable : niveau du pouvoir croissant, puis nom ; les inconnus à la fin. */
export const sortPowerIds = (powerIds, lng = 'fr') => {
  const rank = id => POWERS_BY_ID[id]?.level ?? Infinity;
  const name = id => localized(POWERS_BY_ID[id]?.name, lng) || id;
  return [...powerIds].sort((a, b) => (rank(a) - rank(b)) || name(a).localeCompare(name(b), lng));
};

/** Pouvoirs accessibles pour une discipline à un niveau donné (amalgames inclus si la discipline secondaire suit). */
export const getAvailablePowers = (disciplineId, level, disciplineLevels = {}) => POWERS.filter(item => (
  item.discipline === disciplineId
  && item.level <= level
  && (!item.amalgam || (disciplineLevels[item.amalgam.discipline] || 0) >= item.amalgam.level)
));
